import React, { useState, useEffect } from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Paper,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import { useNavigate, useParams } from "react-router-dom";
import CommonLoader from "../../common/CommonLoader";
import {
  SaveCooler,
  UpdateCooler,
  GetCoolerById,
} from "../../../services/CoolerService";
import { GetAllCoolerProfiles } from "../../../services/CoolerProfileService";

const AddCooler = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    coolerId: "",
    locationName: "",
    coolerProfileId: "",
    clientAccountNumber: "",
    isCheckedIn: true,
  });

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchProfiles();
    if (id) {
      fetchCoolerData(parseInt(id, 10));
    }
  }, [id]);

  const fetchProfiles = async () => {
    try {
      const response = await GetAllCoolerProfiles();
      setProfiles(response?.results || []);
    } catch {
      NotificationManager.error("Error loading cooler profiles");
    }
  };

  const fetchCoolerData = async (coolerId) => {
    setLoading(true);
    try {
      const response = await GetCoolerById(coolerId);
      if (response?.results) {
        const c = response.results;
        setFormData({
          coolerId: c.coolerId || "",
          locationName: c.locationName || "",
          coolerProfileId: c.coolerProfileId ?? "",
          clientAccountNumber: c.clientAccountNumber ?? "",
          isCheckedIn: c.isCheckedIn ?? true,
          id: c.id,
        });
      } else {
        NotificationManager.error("Failed to fetch cooler data");
      }
    } catch {
      NotificationManager.error("Error fetching cooler data");
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.coolerId?.trim())
      newErrors.coolerId = "Cooler ID is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    const coolerData = {
      ...formData,
      coolerProfileId: formData.coolerProfileId
        ? parseInt(formData.coolerProfileId, 10)
        : null,
      clientAccountNumber: formData.clientAccountNumber
        ? parseInt(formData.clientAccountNumber, 10)
        : null,
      id: id ? parseInt(id, 10) : undefined,
    };

    setLoading(true);
    try {
      if (id) {
        await UpdateCooler(coolerData);
        NotificationManager.success("Cooler updated successfully");
      } else {
        await SaveCooler(coolerData);
        NotificationManager.success("Cooler added successfully");
      }
      navigate("/coolers");
    } catch (error) {
      NotificationManager.error(error.message || "Error saving cooler");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCheckboxChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
  };

  const handleCancel = () => navigate("/coolers");

  return (
    <>
      <Paper elevation={3} sx={{ padding: 3, margin: "auto" }}>
        <Typography variant="h5" gutterBottom>
          {id ? "Edit Cooler" : "Add New Cooler"}
        </Typography>

        <Grid container spacing={2}>
          {/* Identity */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Cooler ID (Barcode)"
              value={formData.coolerId}
              onChange={handleChange("coolerId")}
              size="small"
              fullWidth
              required
              disabled={!!id}
              error={!!errors.coolerId}
              helperText={errors.coolerId}
            />
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Location"
              value={formData.locationName || ""}
              onChange={handleChange("locationName")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Profile & Client */}
          <Grid item xs={6} sm={4} md={3}>
            <TextField
              select
              label="Cooler Profile"
              value={formData.coolerProfileId ?? ""}
              onChange={handleChange("coolerProfileId")}
              size="small"
              fullWidth
            >
              <MenuItem value="">-- None --</MenuItem>
              {profiles.map((p) => (
                <MenuItem key={p.coolerProfileId} value={p.coolerProfileId}>
                  {p.profileName} ({p.coolerSize})
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6} sm={4} md={3}>
            <TextField
              label="Client Account #"
              type="number"
              value={formData.clientAccountNumber || ""}
              onChange={handleChange("clientAccountNumber")}
              size="small"
              fullWidth
            />
          </Grid>

          {/* Status */}
          <Grid item xs={12} sm={4} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isCheckedIn}
                  onChange={handleCheckboxChange("isCheckedIn")}
                  color="primary"
                />
              }
              label="Checked In"
            />
          </Grid>

          {/* Actions */}
          <Grid item xs={12} container justifyContent="flex-end" spacing={2}>
            <Grid item>
              <Button onClick={handleCancel} variant="outlined" color="secondary">
                Cancel
              </Button>
            </Grid>
            <Grid item>
              <Button onClick={handleSave} variant="contained" color="primary">
                {id ? "Update" : "Save"}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      <CommonLoader loading={loading} />
    </>
  );
};

export default AddCooler;